import React, { useRef, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';
import './Navigation.css';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Events', to: '/events' },
  { label: 'De-Stress', to: '/destress' },
  { label: 'Team', to: '/team' },
  { label: 'Crisis Help', to: '/crisis' },
  { label: 'Donate', href: 'https://hcb.hackclub.com/donations/start/earmeout', external: true },
];

const Navigation = () => {
  const location = useLocation();
  const [position, setPosition] = useState({ left: 0, width: 0, opacity: 0 });
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <>
      <nav className="navigation-new">
        <div className="navigation-container">
          {/* Brand */}
          <div className="nav-brand-container">
            <img src="/earmeout-bot.png" alt="EarMeOut" className="nav-brand-image" />
            <Link to="/" className="nav-brand">EarMeOut</Link>
          </div>

          {/* Desktop tabs */}
          <SlideTabs position={position} setPosition={setPosition} location={location} />

          {/* Desktop right */}
          <div className="navigation-right nav-desktop-right">
            {!user ? (
              <>
                {location.pathname !== '/' && (
                  <Link to="/login" className="nav-sign-in">Sign In</Link>
                )}
                <Link to="/chat" className="u-btn">
                  <span className="u-btn__top">Get Started</span>
                </Link>
              </>
            ) : (
              <>
                <button onClick={handleLogout} className="logout-btn">
                  <span className="logout-sign">
                    <svg viewBox="0 0 512 512">
                      <path d="M377.9 105.9L500.7 228.7c7.2 7.2 11.3 17.1 11.3 27.3s-4.1 20.1-11.3 27.3L377.9 406.1c-6.4 6.4-15 9.9-24 9.9c-18.7 0-33.9-15.2-33.9-33.9l0-62.1-128 0c-17.7 0-32-14.3-32-32l0-64c0-17.7 14.3-32 32-32l128 0 0-62.1c0-18.7 15.2-33.9 33.9-33.9c9 0 17.6 3.6 24 9.9zM160 96L96 96c-17.7 0-32 14.3-32 32l0 256c0 17.7 14.3 32 32 32l64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-64 0c-53 0-96-43-96-96L0 128C0 75 43 32 96 32l64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32z" />
                    </svg>
                  </span>
                  <span className="logout-text">Logout</span>
                </button>
                <button className="profile-button" onClick={() => navigate('/profile')} aria-label="Go to profile">
                  <User size={22} />
                </button>
              </>
            )}
          </div>

          {/* Hamburger button (mobile only) */}
          <button
            className="nav-hamburger"
            onClick={() => setMenuOpen(o => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="nav-mobile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setMenuOpen(false)}
          >
            <motion.div
              className="nav-mobile-drawer"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="nav-mobile-links">
                {NAV_LINKS.map((link, i) =>
                  link.external ? (
                    <a
                      key={i}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-mobile-link"
                      onClick={() => setMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      key={i}
                      to={link.to}
                      className={`nav-mobile-link ${location.pathname === link.to ? 'active' : ''}`}
                    >
                      {link.label}
                    </Link>
                  )
                )}
              </div>

              <div className="nav-mobile-actions">
                {!user ? (
                  <>
                    <Link to="/login" className="nav-mobile-signin" onClick={() => setMenuOpen(false)}>
                      Sign In
                    </Link>
                    <Link to="/chat" className="nav-mobile-cta" onClick={() => setMenuOpen(false)}>
                      Get Started
                    </Link>
                  </>
                ) : (
                  <>
                    <button className="nav-mobile-signin" onClick={() => { navigate('/profile'); setMenuOpen(false); }}>
                      Profile
                    </button>
                    <button className="nav-mobile-cta nav-mobile-logout" onClick={() => { handleLogout(); setMenuOpen(false); }}>
                      Logout
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const SlideTabs = ({ position, setPosition, location }) => {
  return (
    <ul
      onMouseLeave={() => setPosition(pv => ({ ...pv, opacity: 0 }))}
      className="slide-tabs"
    >
      <Tab setPosition={setPosition} to="/" isActive={location.pathname === '/'}>Home</Tab>
      <Tab setPosition={setPosition} to="/events" isActive={location.pathname === '/events'}>Events</Tab>
      <Tab setPosition={setPosition} to="/destress" isActive={location.pathname === '/destress'}>De-Stress</Tab>
      <Tab setPosition={setPosition} to="/team" isActive={location.pathname === '/team'}>Team</Tab>
      <Tab setPosition={setPosition} to="/crisis" isActive={location.pathname === '/crisis'}>Crisis Help</Tab>
      <Tab setPosition={setPosition} href="https://hcb.hackclub.com/donations/start/earmeout" external>Donate</Tab>
      <Cursor position={position} />
    </ul>
  );
};

const Tab = ({ children, setPosition, to, href, external, isActive }) => {
  const ref = useRef(null);

  const handleMouseEnter = () => {
    if (!ref?.current) return;
    const { width } = ref.current.getBoundingClientRect();
    setPosition({ left: ref.current.offsetLeft, width, opacity: 1 });
  };

  if (external && href) {
    return (
      <li ref={ref} onMouseEnter={handleMouseEnter} className="slide-tab">
        <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
      </li>
    );
  }

  return (
    <li ref={ref} onMouseEnter={handleMouseEnter} className="slide-tab">
      <Link to={to}>{children}</Link>
    </li>
  );
};

const Cursor = ({ position }) => (
  <motion.li animate={{ ...position }} className="slide-cursor" />
);

export default Navigation;
